import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

// Popover riutilizzabile per esportare CSV con selettore di periodo
// (Settimana / Mese / Tutti). Quando l'utente clicca "Scarica" viene
// invocata la callback `onDownload(period, offset, label)`:
//   - period: "week" | "month" | "all"
//   - offset: numero intero (0 = corrente, -1 = precedente, …)
//   - label: stringa human-readable del periodo (utile per il filename)
// Il caller è responsabile di fetchare/produrre il CSV.
export default function CsvExportPopover({
  title = "Esporta CSV",
  triggerLabel = "Scarica CSV",
  triggerClassName,
  // tema bottone trigger: "ghost-on-dark" (header sfondo blu) | "outline" (bottone in card chiara)
  triggerVariant = "ghost-on-dark",
  onDownload,
  // Periodi disabilitati: array di id da non mostrare (es. ["all"])
  disabledPeriods = [],
  defaultPeriod = "month",
  testIdPrefix = "csv-export",
}) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [period, setPeriod] = useState(defaultPeriod);
  const [offset, setOffset] = useState(0);
  const popoverRef = useRef(null);

  // Click-outside per chiudere il popover.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const getLabel = () => {
    const now = new Date();
    if (period === "week") {
      const monday = new Date(now);
      const day = monday.getDay() || 7;
      monday.setDate(monday.getDate() - day + 1 + offset * 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return `${monday.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} – ${sunday.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}`;
    }
    if (period === "month") {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    }
    return "Tutti i dati";
  };

  const handleClick = async () => {
    setDownloading(true);
    try {
      await onDownload?.(period, offset, getLabel());
      setOpen(false);
    } finally {
      setDownloading(false);
    }
  };

  const periods = [
    { id: "week", label: "Settimana" },
    { id: "month", label: "Mese" },
    { id: "all", label: "Tutti" },
  ].filter((p) => !disabledPeriods.includes(p.id));

  // Default styling del trigger in base al contesto.
  const defaultTrigger =
    triggerVariant === "outline"
      ? "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 h-9 px-3 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors"
      : "inline-flex items-center gap-2 px-3 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm text-sm font-medium";

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-trigger`}
        className={triggerClassName || defaultTrigger}
      >
        <Download className="w-4 h-4" />
        <span className={triggerVariant === "outline" ? "" : "hidden sm:inline"}>{triggerLabel}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-50 text-slate-800"
          data-testid={`${testIdPrefix}-panel`}
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Periodo</label>
          <div className={`grid gap-1.5 mb-3`} style={{ gridTemplateColumns: `repeat(${periods.length}, minmax(0,1fr))` }}>
            {periods.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { setPeriod(o.id); setOffset(0); }}
                data-testid={`${testIdPrefix}-period-${o.id}`}
                className={`h-9 text-xs rounded-lg border font-medium transition-colors ${
                  period === o.id
                    ? "bg-[#3B42DD] border-[#3B42DD] text-white"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {period !== "all" && (
            <>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                {period === "week" ? "Settimana" : "Mese"}
              </label>
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-2 mb-3">
                <button
                  type="button"
                  onClick={() => setOffset(offset - 1)}
                  className="w-8 h-8 rounded-md hover:bg-white text-slate-600 flex items-center justify-center"
                  data-testid={`${testIdPrefix}-offset-prev`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="text-sm font-semibold text-slate-700 text-center flex-1 capitalize">
                  {getLabel()}
                </div>
                <button
                  type="button"
                  onClick={() => offset < 0 && setOffset(offset + 1)}
                  disabled={offset >= 0}
                  className="w-8 h-8 rounded-md hover:bg-white text-slate-600 flex items-center justify-center disabled:opacity-30"
                  data-testid={`${testIdPrefix}-offset-next`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              className="flex-1"
              data-testid={`${testIdPrefix}-cancel`}
            >
              Annulla
            </Button>
            <Button
              size="sm"
              onClick={handleClick}
              disabled={downloading}
              data-testid={`${testIdPrefix}-confirm`}
              className="flex-1 bg-[#3B42DD] hover:bg-[#2f36b8] text-white"
            >
              <Download className={`w-4 h-4 mr-1.5 ${downloading ? "animate-pulse" : ""}`} />
              {downloading ? "Download..." : "Scarica"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
